import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon, type IconName } from './icons';
import { cx } from './primitives';
import { ListboxItem, portalHost, useAnchoredPanel, type ListOption } from './Listbox';
export interface TabItem {
    value: string;
    label: string;
    icon?: IconName;
    disabled?: boolean;
    content?: React.ReactNode;
}
export interface TabsProps {
    label: string;
    items: TabItem[];
    value: string;
    onChange: (value: string) => void;
}
export function Tabs({ label, items, value, onChange }: TabsProps) {
    const id = useId();
    const refs = useRef<(HTMLButtonElement | null)[]>([]);
    function navigate(event: React.KeyboardEvent, index: number) { const enabled = items.map((t, i) => ({ t, i })).filter(x => !x.t.disabled); let pos = enabled.findIndex(x => x.i === index); if (event.key === 'ArrowRight')
        pos = (pos + 1) % enabled.length;
    else if (event.key === 'ArrowLeft')
        pos = (pos - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home')
        pos = 0;
    else if (event.key === 'End')
        pos = enabled.length - 1;
    else
        return; event.preventDefault(); const next = enabled[pos]; onChange(next.t.value); refs.current[next.i]?.focus(); }
    return <div className="bs-tabs"><div className="bs-tablist" role="tablist" aria-label={label}>{items.map((t, i) => <button type="button" id={`${id}-tab-${t.value}`} aria-controls={t.content != null ? `${id}-panel-${t.value}` : undefined} ref={el => { refs.current[i] = el; }} key={t.value} role="tab" disabled={t.disabled} tabIndex={t.value === value ? 0 : -1} aria-selected={value === t.value} onClick={() => onChange(t.value)} onKeyDown={e => navigate(e, i)}>{t.icon && <Icon name={t.icon} size={15}/>} {t.label}</button>)}</div>{items.filter(t => t.content != null).map(t => <div hidden={t.value !== value} key={t.value} role="tabpanel" tabIndex={0} aria-labelledby={`${id}-tab-${t.value}`} id={`${id}-panel-${t.value}`} className="bs-tabpanel">{t.content}</div>)}</div>;
}
export function SegmentedControl({ label, items, value, onChange }: TabsProps) {
    const name = useId();
    return <fieldset className="bs-segment" aria-label={label}>{items.map(t => <label key={t.value} className={cx(value === t.value && 'is-selected', t.disabled && 'is-disabled')}><input type="radio" name={name} value={t.value} checked={value === t.value} onChange={() => onChange(t.value)} disabled={t.disabled}/>{t.icon && <Icon name={t.icon} size={15}/>}<span>{t.label}</span></label>)}</fieldset>;
}
export function Breadcrumbs({ items }: {
    items: {
        label: string;
        onClick?: () => void;
    }[];
}) { return <nav aria-label="Breadcrumb" className="bs-breadcrumbs">{items.map((x, i) => <React.Fragment key={`${x.label}-${i}`}>{i > 0 && <Icon name="right" size={12}/>}{x.onClick ? <button type="button" onClick={x.onClick}>{x.label}</button> : <span aria-current={i === items.length - 1 ? 'page' : undefined}>{x.label}</span>}</React.Fragment>)}</nav>; }
export interface NavItem {
    id: string;
    label: string;
    icon?: IconName;
    complete?: boolean;
    count?: number;
}
export function SidebarNav({ items, value, onChange, label = 'Production' }: {
    items: NavItem[];
    value: string;
    onChange: (id: string) => void;
    label?: string;
}) {
    return <nav className="bs-sidebar-nav" aria-label={label}><p className="bs-eyebrow">{label}</p>{items.map((x, i) => <button type="button" key={x.id} aria-current={value === x.id ? 'page' : undefined} onClick={() => onChange(x.id)}>{x.icon ? <Icon name={x.icon} size={16}/> : <span className="bs-step-number">{x.complete ? <Icon name="check" size={13}/> : String(i + 1).padStart(2, '0')}</span>}<span>{x.label}</span>{x.count != null && <small>{x.count}</small>}</button>)}</nav>;
}
export function Stepper({ steps, current, onChange }: {
    steps: {
        id: string;
        label: string;
        complete?: boolean;
    }[];
    current: string;
    onChange?: (id: string) => void;
}) { return <ol className="bs-steps">{steps.map((x, i) => <li key={x.id} className={cx(x.id === current && 'is-current', x.complete && 'is-complete')}><button type="button" onClick={() => onChange?.(x.id)} disabled={!onChange} aria-current={x.id === current ? 'step' : undefined}><span>{x.complete ? <Icon name="check" size={13}/> : i + 1}</span>{x.label}</button></li>)}</ol>; }
export interface MenuItem {
    label: string;
    icon?: IconName;
    description?: string;
    onSelect: () => void;
    danger?: boolean;
    disabled?: boolean;
    selected?: boolean;
}
export function DropdownMenu({ label = 'More options', items, children }: {
    label?: string;
    items: MenuItem[];
    children?: React.ReactNode;
}) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const trigger = useRef<HTMLButtonElement>(null);
    const menu = useRef<HTMLDivElement>(null);
    const id = useId();
    const style = useAnchoredPanel(open, trigger, menu, 'end');
    useEffect(() => {
        if (!open) return;
        menu.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
        function close(e: PointerEvent) {
            if (!ref.current?.contains(e.target as Node) && !menu.current?.contains(e.target as Node)) setOpen(false);
        }
        function onKey(e: KeyboardEvent) {
            if (e.key === 'Escape') {
                e.preventDefault();
                e.stopImmediatePropagation();
                setOpen(false);
                trigger.current?.focus();
            }
        }
        document.addEventListener('pointerdown', close);
        window.addEventListener('keydown', onKey, true);
        return () => {
            document.removeEventListener('pointerdown', close);
            window.removeEventListener('keydown', onKey, true);
        };
    }, [open]);
    return <div ref={ref} className="bs-dropdown"><button type="button" ref={trigger} className={cx('bs-button', 'bs-button--secondary', 'bs-button--md', !children && 'bs-icon-button')} aria-label={label} aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined} onClick={() => setOpen(v => !v)} onKeyDown={e => { if (e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
    } }}>{children || <Icon name="more"/>}</button>{open && createPortal(<div ref={menu} role="menu" id={id} aria-label={label} className="bs-theme bs-listbox-panel bs-menu" style={style} onKeyDown={e => { const options = Array.from(menu.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') || []); const i = options.indexOf(document.activeElement as HTMLButtonElement); if (e.key === 'Tab') {
        setOpen(false);
    }
    else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) {
        e.preventDefault();
        const next = e.key === 'Home' ? 0 : e.key === 'End' ? options.length - 1 : e.key === 'ArrowDown' ? (i + 1) % options.length : (i - 1 + options.length) % options.length;
        options[next]?.focus();
    } }}>{items.map((x, i) => {
        const option: ListOption = { value: String(i), label: x.label, icon: x.icon, description: x.description, disabled: x.disabled };
        return <ListboxItem key={i} role="menuitem" className={cx(x.danger && 'bs-error')} option={option} selected={Boolean(x.selected)} onSelect={() => { setOpen(false); trigger.current?.focus(); x.onSelect(); }} />;
    })}</div>, portalHost(trigger.current))}</div>;
}
